import React, { useState } from "react";
import ReactDOM from "react-dom";
import { message, Typography, Avatar, Tooltip } from "antd";
import ChatEditor from "./ChatEditor";
import CommentCard from "./CommentCard";
import Cookies from "js-cookie";
import { convertDateToUserTimezone } from "../Components/Utility";
import { DateTimeFormat } from "../Components/Constant";

const { Title, Text } = Typography;

export default function Editor({
  handleSend,
  users,
  comments,
  isOpenChatbox,
  handleReact,
  handleReply,
}) {
  const [preview, setPreview] = useState({
    visible: false,
    type: null,
    src: null,
    filename: null,
  });

  const [replyingTo, setReplyingTo] = useState(null);
  const [showReplies, setShowReplies] = useState({}); // 👈 To toggle replies visibility

  const userId = Cookies.get("userId");

  const handlePreviewClose = () =>
    setPreview({ visible: false, type: null, src: null, filename: null });

  const toggleReplies = (commentId) => {
    setShowReplies((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  return (
    <div
      style={{
        padding: 0,
        maxWidth: 900,
        margin: 0,
        minHeight: "400px",
        maxHeight: "800px",
      }}
    >
      {/* --- New Message Editor --- */}
      <ChatEditor onSend={handleSend} users={users} showTools={isOpenChatbox} />

      {/* --- Comments Section --- */}
      <div
        style={{
          marginTop: 5,
          minHeight: "100px",
          overflowY: "auto",
          maxHeight: "500px",
        }}
      >
        <Title level={4} style={{ marginBottom: 5, marginTop: 5 }}>
          Latest Comments
        </Title>

        {[...comments]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .map((m) => {
            const repliesCount = m.replies?.length || 0;
            const isRepliesVisible = showReplies[m.id];
            const replies = m.replies || [];
            const hasReplies = replies.length > 0;
            const lastTwo = replies.slice(-2);
            const lastReply = replies[replies.length - 1];

            return (
              <div key={m.id} style={{ marginBottom: 1 }}>
                {/* 🧩 Parent Comment */}
                <CommentCard
                  comment={m}
                  users={users}
                  onReact={handleReact}
                  handleSend={({ html, files }) => {
                    handleSend({ html, files, id: m.id });
                  }}
                  setReplyingTo={setReplyingTo}
                />

                {/* 💬 Replies count below reactions */}
                {hasReplies && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      marginTop: 0,
                      marginLeft: "40px",
                      cursor: "pointer",
                      fontSize: "13px",
                      color: "#1677ff",
                      fontWeight: "500",
                    }}
                    onClick={() => toggleReplies(m.id)}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      {lastTwo.map((r) => (
                        <Tooltip title={r.user?.name} key={r.id}>
                          {r.user?.image ? (
                            <Avatar
                              size={18}
                              src={r.user.image}
                            />
                          ) : (
                            <Avatar
                              size={18}
                              style={{
                                backgroundColor: "#52c41a", // ✅ green color
                                fontSize: "10px", // ✅ smaller character inside
                                fontWeight: "500",
                              }}
                            >
                              {r.user?.name ? r.user.name.charAt(0).toUpperCase() : "?"}
                            </Avatar>
                          )}
                        </Tooltip>
                      ))}
                    </div>
                    {replies.length} replies

                    {lastReply?.created_at && (
                      <span style={{ color: "#888", fontWeight: "normal" }}>
                        • Last reply {convertDateToUserTimezone(lastReply.created_at, DateTimeFormat)}
                      </span>
                    )}
                  </div>

                )}


                {/* 🧩 Replies List (toggleable) */}
                {isRepliesVisible && (
                  <div
                    style={{
                      marginLeft: 50,
                      marginTop: 2,
                      borderLeft: "2px solid #eee",
                      paddingLeft: 12,
                    }}
                  >


                    {Array.isArray(m.replies) &&
                      m.replies.map((r) => (
                        <CommentCard
                          key={r.id}
                          comment={r}
                          users={users}
                          onReact={handleReact}
                          isReply={true}
                          handleSend={({ html, files }) =>
                            handleReply({
                              html,
                              files,
                              parentId: m.id,
                              replyId: r.id,
                            })
                          }
                          setReplyingTo={setReplyingTo}
                        />
                      ))}
                  </div>
                )}

                {/* 🧩 Replying editor */}
                {replyingTo === m.id && (
                  <div
                    style={{
                      marginTop: 2,
                      marginLeft: 50,
                      padding: "8px 12px",
                      borderLeft: "2px solid #1677ff20",
                    }}
                  >
                    <div
                      style={{
                        fontSize: 13,
                        color: "#555",
                        marginBottom: 4,
                        fontWeight: 500,
                      }}
                    >
                      Replying to{" "}
                      <span style={{ color: "#1677ff" }}>
                        {m.user?.name || "Unknown User"}
                      </span>
                    </div>

                    <ChatEditor
                      onSend={({ html, files }) => {
                        handleReply({
                          html,
                          files,
                          parentId: m.id,
                          replyId: undefined,
                        });
                        setReplyingTo(null);
                      }}
                      showTools={true}
                      users={users}
                      onCancel={() => setReplyingTo(null)}
                      setReplyingTo={setReplyingTo}
                    />
                  </div>
                )}
              </div>
            );
          })}

        {/* --- File Preview Modal --- */}
        {preview.visible &&
          ReactDOM.createPortal(
            <div
              style={{
                position: "fixed",
                left: 0,
                top: 0,
                width: "100vw",
                height: "100vh",
                background: "rgba(0,0,0,0.5)",
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={handlePreviewClose}
            >
              <div
                style={{
                  background: "#fff",
                  padding: 24,
                  borderRadius: 8,
                  minWidth: 320,
                  minHeight: 120,
                  maxWidth: "90vw",
                  maxHeight: "90vh",
                  position: "relative",
                  boxSizing: "border-box",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  style={{
                    position: "absolute",
                    right: 12,
                    top: 8,
                    cursor: "pointer",
                    fontSize: 22,
                    color: "#888",
                  }}
                  onClick={handlePreviewClose}
                >
                  &times;
                </div>
                <div style={{ marginBottom: 12, fontWeight: 500 }}>
                  {preview.filename}
                </div>
                {preview.type === "image" && (
                  <img
                    src={preview.src}
                    alt={preview.filename}
                    style={{
                      maxWidth: "80vw",
                      maxHeight: "70vh",
                      borderRadius: 4,
                    }}
                  />
                )}
                {preview.type === "pdf" && (
                  <iframe
                    src={preview.src}
                    title={preview.filename}
                    style={{
                      width: "70vw",
                      height: "70vh",
                      border: "none",
                    }}
                  />
                )}
              </div>
            </div>,
            document.body
          )}
      </div>
    </div>
  );
}
